import React from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Container,
    Row,
    Col,
    Button,
    Form,
    Accordion,
    OverlayTrigger,
    Popover,
    PopoverBody,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";

import confirm from "components/Confirm";
import disableModuleConfirm from "components/DisableModuleConfirm";
import Categories from "components/Categories";
import Toast from "services/Toast";
import Loading from "scenes/Loading";
import Emitter from "services/Emitter";
import ProcessingButton from "components/ProcessingButton";
import BrandService from "services/BrandService";
import Regions from "components/Regions";
import OrganizationSearch from "components/OrganizationSearch";
import MerchantSearch from "components/MerchantSearch";
import AmazonService from "services/AmazonService";
import "./Profile.css";
import PartnerService from "services/PartnerService";
import EbayService from "services/EbayService";
import WalmartService from "services/WalmartService";

/**
 * Flips a switch value. Anything that is not a switch is passed through, so a
 * text or option value can never be mangled by an inverted setting.
 * @param {string} value
 * @returns {string}
 */
const invertSwitch = (value) => {
    if (value !== "on" && value !== "off") {
        return value;
    }

    return value === "on" ? "off" : "on";
};

/**
 * Copies a catalog down to the arrays the merchant-picker composition edits, so
 * a fresh object identity reaches setState and the previous pillars are left
 * untouched. Deep enough to cover the nesting the catalog actually has.
 * @param {Array} pillars
 * @returns {Array}
 */
const copyPillars = (pillars) =>
    pillars.map((pillar) => ({
        ...pillar,
        modules: (pillar.modules ?? []).map((module) => ({
            ...module,
            settings: (module.settings ?? []).map((setting) => ({
                ...setting,
            })),
        })),
    }));

/**
 * Explains why a control the retired-module rule locked cannot be touched.
 * The hint hangs off a wrapper because a disabled input fires no pointer events
 * of its own, so an overlay attached to it would never open. The wrapper takes
 * the control's place in the surrounding flex row, so it has to carry the sizing
 * class the control relied on.
 * @param {JSX.Element} control the rendered form control
 * @param {string} key React key for the wrapper
 * @param {boolean} inactive whether the retired-module rule applies
 * @param {string} className sizing the wrapper inherits from the control
 * @returns {JSX.Element}
 */
const withInactiveHint = (control, key, inactive, className) => {
    if (!inactive) {
        return control;
    }

    return (
        <OverlayTrigger
            key={key}
            placement="bottom"
            trigger={["hover", "focus"]}
            overlay={
                <Popover>
                    <PopoverBody>
                        The module associated to this setting is disabled
                    </PopoverBody>
                </Popover>
            }
        >
            <span className={className}>{control}</span>
        </OverlayTrigger>
    );
};

/**
 * Show a CRUD for Brands
 * @component
 * @category Scenes
 * @subcategory Brands
 */
class Profile extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            id: props.brandId,
            row: {
                categories: [],
                crawlingRegions: [],
            },
            settings: {},
            loading: true,
            editing: false,
            updating: false,
            problems: false,
            pillars: [],
            catalogFailed: false,
            copyMismatch: false,
            loadFailed: false,
        };

        this.modulesToUnassign = {};
        this.mounted = false;

        this.handleChange = this.handleChange.bind(this);
        this.handleChangeSetting = this.handleChangeSetting.bind(this);
        this.handleChangeRegions = this.handleChangeRegions.bind(this);
        this.handleOrgChange = this.handleOrgChange.bind(this);
        this.handleMerchantChange = this.handleMerchantChange.bind(this);
        this.dropEdition = this.dropEdition.bind(this);
        this.startEdition = this.startEdition.bind(this);
        this.saveChanges = this.saveChanges.bind(this);
        this.reload = this.reload.bind(this);
        this.retryLoad = this.retryLoad.bind(this);
        this.retryCatalog = this.retryCatalog.bind(this);
        this.PPDStatus = this.PPDStatus.bind(this);
    }

    handleOrgChange = (org) => {
        let row = this.state.row;
        row.org_id = org.length ? org[0].id : 0;

        if (row.org_id !== this.original.org_id) {
            this.changes.fields.org_id = row.org_id;
        } else {
            delete this.changes.fields.org_id;
        }

        this.setState({ row, problems: this.validateRow(row) });
    };

    async adjustAmazonVisibilitySuite(row = null, catalog = null) {
        const data = row || this.state.row;
        // Copied, because the merchant pickers are spliced into these arrays. On
        // the state.pillars path an in-place edit would hand setState back the
        // same reference it already holds, so the re-render would rely on this
        // component never being memoised.
        const pillars = copyPillars(catalog || this.state.pillars);
        const selectedCountryIDs = data.crawlingRegions.map(
            (obj) => Regions.GetByID(obj.value).country
        );
        // Independent lookups. Each memoises its resolved list, but only on the
        // success path — while they keep failing, every call re-requests.
        const [amazonWebsites, ebayWebsites, walmartWebsites] =
            await Promise.all([
                AmazonService.FilterByCountryID(selectedCountryIDs),
                EbayService.FilterByCountryID(selectedCountryIDs),
                WalmartService.FilterByCountryID(selectedCountryIDs),
            ]);

        pillars.forEach((pillar) => {
            if (pillar.code !== "visibility_pillar") {
                return;
            }

            pillar.modules.forEach((module) => {
                if (module.code === "amazon_visibility_suite") {
                    this.adjustMarketplaceMerchantSettings(
                        module.settings,
                        amazonWebsites,
                        "amazon_merchant",
                        false
                    );
                }

                if (module.code === "ebay_insights_module") {
                    this.adjustMarketplaceMerchantSettings(
                        module.settings,
                        ebayWebsites,
                        "ebay_merchants",
                        true
                    );
                }

                if (module.code === "walmart_insights_module") {
                    this.adjustMarketplaceMerchantSettings(
                        module.settings,
                        walmartWebsites,
                        "walmart_merchants",
                        true
                    );
                }
            });
        });

        return pillars;
    }

    /**
     * Re-compose the amazon_merchant settings inside pillars based on the selected countries
     * Adjust merchant settings (e.g., amazon_merchant, ebay_merchant, walmart_merchants) based on selected websites.
     * @param {Array} settings - Existing settings (e.g., from pillars)
     * @param {Array} websites - Array of website objects to apply (e.g., [{id, name, country_id}])
     * @param {string} marketplaceCode - e.g., "amazon_merchant", "ebay_merchant", "walmart_merchants"
     * @param {boolean} isMultiple - If the search component is multiple or not
     * @param {string} labelSuffix - e.g., "'s Seller" or other label customization
     */
    adjustMarketplaceMerchantSettings(
        settings,
        websites,
        marketplaceCode,
        isMultiple = false,
        labelSuffix = "'s Seller"
    ) {
        // The marketplace services resolve to false when their lookup fails.
        if (!Array.isArray(websites)) {
            return;
        }

        const websiteIds = websites.map((obj) => obj.id);

        for (let i = 0; i < settings.length; i++) {
            const setting = settings[i];
            if (setting.code !== marketplaceCode) {
                continue;
            }

            if (!websiteIds.includes(setting.website_id)) {
                settings.splice(i--, 1);
                continue;
            }

            websiteIds.splice(websiteIds.indexOf(setting.website_id), 1);
        }

        websites.forEach((website) => {
            if (!websiteIds.includes(website.id)) {
                return;
            }

            settings.push({
                code: marketplaceCode,
                country_id: website.country_id,
                website_id: website.id,
                label: `${website.name}${labelSuffix}`,
                type: "merchant",
                book: "country",
                multiple: isMultiple,
            });
        });

        settings.sort((a, b) => (a.website_id < b.website_id ? -1 : 1));
    }

    validateRow(row) {
        let problems = {};
        if (row.org_id === 0) {
            problems.org_id = true;
        }

        if ((row.store_name ?? "").trim().length === 0) {
            problems.store_name = true;
        }
        return Object.keys(problems).length === 0 ? false : problems;
    }

    /**
     * Records one setting change against the row and the pending change set. The
     * caller owns the re-render, so a cascade can write several settings and pay
     * for a single render instead of one per setting.
     * @param {string} name setting name
     * @param {string} value value as the control presents it
     * @param {string} book brand, store or country
     * @param {boolean} isCheckbox whether the control is a switch
     * @param {boolean} inverted whether the stored value is the opposite
     * @returns {object} the mutated row, ready to hand to setState
     */
    updateRow(name, value, book, isCheckbox = false, inverted = false) {
        const bookSettings = book + "Settings"; // brandSettings or storeSettings
        const row = this.state.row;
        // Everything below works in stored space, which an inverted setting
        // writes as the opposite of what the switch shows.
        const stored = inverted ? invertSwitch(value) : value;
        const mapBook =
            "map" +
            bookSettings.charAt(0).toUpperCase() +
            bookSettings.slice(1); // mapBrandSettings or mapStoreSettings
        const rowId =
            typeof this[mapBook][name] === "number" ? this[mapBook][name] : -1;

        row[bookSettings][name] = stored;

        if (rowId === -1) {
            if (stored === "off" || stored === "") {
                delete this.changes[book][name];
                return row;
            }

            this.changes[book][name] = { name, id: 0, value: stored };
            return row;
        }

        const setting = this[bookSettings][rowId];
        const oldValue = isCheckbox
            ? /(1|on)/i.test(setting.value)
                ? "on"
                : "off"
            : setting.value;

        if (stored === oldValue) {
            delete this.changes[book][name];
            return row;
        }

        this.changes[book][name] = { name, id: setting.id, value: stored };
        return row;
    }

    handleChange(evt) {
        let key = evt.target.name,
            row = this.state.row,
            isCheckbox = evt.target.className.match(/check/),
            value = isCheckbox
                ? evt.target.checked
                    ? 1
                    : 0
                : evt.target.value,
            oldValue = isCheckbox
                ? /(1|on)/i.test(this.original[key])
                    ? 1
                    : 0
                : this.original[key];

        if (oldValue !== value) {
            this.changes.fields[key] = value;
        } else {
            delete this.changes.fields[key];
        }
        row[key] = value;
        this.setState({ row, problems: this.validateRow(row) });
    }

    handleMerchantChange(selected) {
        if (!Array.isArray(selected)) {
            selected = selected ? [selected] : [];
        }

        const validSelected = selected.filter(
            (merchant) => merchant && merchant.id
        );
        const value =
            validSelected.length > 1
                ? validSelected
                : validSelected.length === 1
                ? validSelected[0]
                : null;

        this.setState({
            row: this.updateRow(this.merchantOnFocus, value, "country"),
        });
    }

    getModuleObject(pillar, module) {
        if (pillar === null || module === null) {
            return {};
        }
        const pillarObj = this.state.pillars.find((p) => p.code === pillar);
        if (!pillarObj) {
            return {};
        }

        const moduleObj = pillarObj.modules.find((m) => m.code === module);
        if (moduleObj) {
            return moduleObj;
        }

        for (const mod of pillarObj.modules) {
            if (mod.settings && mod.settings.length) {
                const setting = mod.settings.find((s) => s.code === module);
                if (setting) {
                    return setting;
                }
            }
        }

        return {};
    }

    async checkConfirmation(pillar, module, value) {
        const config = this.getModuleObject(pillar, module),
            confirmationMessage =
                typeof config.confirm !== "undefined" &&
                typeof config.confirm[value] !== "undefined"
                    ? config.confirm[value]
                    : null;

        if (confirmationMessage === null) {
            return true;
        }

        let title = "Are you sure?",
            message = confirmationMessage,
            confirmButton = "Yes",
            cancelButton = "No",
            size = null;

        if (typeof confirmationMessage === "object") {
            title = confirmationMessage.title || title;
            message = confirmationMessage.message || "";
            confirmButton = confirmationMessage.yes || confirmButton;
            cancelButton = confirmationMessage.no || cancelButton;
            size = confirmationMessage.size || size;
        }

        return await confirm(title, confirmButton, cancelButton, {
            message: message,
            size,
            enableEscape: false,
        });
    }

    async checkDisableConfirmation(pillar, module) {
        const config = this.getModuleObject(pillar, module);

        if (
            typeof config.confirmDisable === "undefined" ||
            !config.confirmDisable
        ) {
            return true;
        }

        return await disableModuleConfirm();
    }

    displayFeedbackMessage(pillar, module, value) {
        const config = this.getModuleObject(pillar, module),
            feedbackMessage =
                typeof config.feedback !== "undefined" &&
                typeof config.feedback[value] !== "undefined"
                    ? config.feedback[value]
                    : null;

        if (feedbackMessage === null) {
            return;
        }
        Toast.info(feedbackMessage, 7500);
    }

    async handleChangeSetting(evt, settings = []) {
        const isCheckbox = evt.target.className.match(/check/) !== null,
            book = evt.target.getAttribute("book"), // brand or store
            name = evt.target.getAttribute("name"),
            pillar = evt.target.getAttribute("pillar"),
            value = isCheckbox
                ? evt.target.checked
                    ? "on"
                    : "off"
                : evt.target.value;

        if (value === "off") {
            const disableResponse = await this.checkDisableConfirmation(
                pillar,
                name
            );

            if (disableResponse === false) {
                return;
            }

            if (disableResponse === "disable_only") {
                this.modulesToUnassign[name] = false;
                this.cascadableSettings(settings).forEach((setting) => {
                    this.modulesToUnassign[setting.code] = false;
                });
            }

            if (disableResponse === "disable_unassign") {
                this.modulesToUnassign[name] = true;
                this.cascadableSettings(settings).forEach((setting) => {
                    this.modulesToUnassign[setting.code] = true;
                });
            }
        }

        if (value === "on") {
            this.modulesToUnassign[name] = false;
        }

        if (value !== "off") {
            if (!(await this.checkConfirmation(pillar, name, value))) {
                return;
            }
        }

        // The confirmation dialogs above are mounted outside the router tree, so
        // they outlive this tab and the user can answer one after leaving.
        if (!this.mounted) {
            return;
        }

        this.displayFeedbackMessage(pillar, name, value);
        const row = this.updateRow(
            name,
            value,
            book,
            isCheckbox,
            this.getModuleObject(pillar, name).inverted
        );

        if (settings.length && value === "off") {
            const settingValue = isCheckbox ? "off" : "";
            this.cascadableSettings(settings).forEach((setting) => {
                this.updateRow(
                    setting.code,
                    settingValue,
                    setting.book,
                    false,
                    setting.inverted
                );
            });
        }

        this.safeSetState({ row });
    }

    async handleChangeRegions(selected) {
        const row = this.state.row;
        row["crawlingRegions"] = selected;
        const pillars = await this.adjustAmazonVisibilitySuite(row);

        if (!this.mounted) {
            return;
        }

        this.setState({ row, pillars }, () => {
            const newRegions = selected.map((obj) => obj.value).sort(),
                oldRegions = this.original.crawlingRegions
                    .map((obj) => obj.value)
                    .sort();
            if (JSON.stringify(newRegions) === JSON.stringify(oldRegions)) {
                this.changes.regions = null;
            } else {
                this.changes.regions = newRegions;
            }
        });
    }

    polishData(row) {
        row.categories = Categories.Selected(row.categories);
        row.crawlingRegions = Regions.Selected(row.crawlingRegions);
        this.storeSettings = [];
        this.mapStoreSettings = {};
        let storeSettings = {};
        if (typeof row.store === "object" && row.store.length) {
            row.store.forEach((setting, i) => {
                this.storeSettings.push(setting);
                this.mapStoreSettings[setting.name] = i;
                storeSettings[setting.name] = setting.value;
            });
        }
        row.storeSettings = storeSettings;
        this.brandSettings = [];
        this.mapBrandSettings = {};
        let brandSettings = {};
        if (typeof row.brand === "object" && row.brand.length) {
            row.brand.forEach((setting, i) => {
                this.brandSettings.push(setting);
                this.mapBrandSettings[setting.name] = i;
                brandSettings[setting.name] = setting.value;
            });
        }
        row.brandSettings = brandSettings;
        this.countrySettings = [];
        this.mapCountrySettings = {};
        let countrySettings = {};
        if (typeof row.country === "object" && row.country.length) {
            row.country.forEach((setting, i) => {
                const key = `${setting.code}-${setting.country_id}`;
                this.countrySettings.push(setting);
                this.mapCountrySettings[key] = i;
                countrySettings[key] = setting;
            });
        }
        row.countrySettings = countrySettings;
        delete row.store;
        delete row.brand;
        delete row.country;
        return row;
    }

    /**
     *
     * @param {string} book store or brand
     * @param {string} key setting code
     * @param {*} defaultValue
     * @returns {*}
     */
    getSetting(book, key, defaultValue) {
        book += "Settings";
        if (
            typeof this.state.row === "object" &&
            typeof this.state.row[book] === "object" &&
            typeof this.state.row[book][key] !== "undefined"
        ) {
            return this.state.row[book][key];
        }
        return defaultValue;
    }

    getSettingBool(book, key, inverted = false) {
        const enabled = /(on|1|true)/i.test(this.getSetting(book, key, ""));

        return inverted ? !enabled : enabled;
    }

    /**
     * A module the platform has retired (modules.active = false) stays visible
     * so its current value can be read, but can no longer be changed.
     * @param {object} setting module or sub-setting from the pillar catalog
     * @returns {boolean}
     */
    isEditable(setting) {
        if (this.state.copyMismatch) {
            return false;
        }

        return (
            this.state.editing &&
            !this.state.updating &&
            setting.active !== false
        );
    }

    /**
     * The sub-settings that switching a module off is allowed to write through to.
     * Retired ones are locked in the form, so the cascade must not write them
     * either — the backend stores whatever it is handed, without re-checking
     * modules.active. Merchant pickers are stored per country, under a key the
     * cascade cannot address, so they are left for the user to clear.
     * @param {Array} settings sub-settings of the module being switched off
     * @returns {Array}
     */
    cascadableSettings(settings) {
        return settings.filter(
            (setting) => setting.type !== "merchant" && setting.active !== false
        );
    }

    getPillarStatus(settings) {
        if (!Array.isArray(settings)) {
            return false;
        }

        for (const setting of settings) {
            if (
                this.getSettingBool(
                    setting.book,
                    setting.code,
                    setting.inverted
                )
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * setState that no-ops once the tab is gone. Index mounts every brand tab at
     * once and the user can leave while the brand and catalog requests are still
     * in flight, so these callbacks can outlive the component.
     * @param {object} partial
     */
    safeSetState(partial) {
        if (!this.mounted) {
            return;
        }

        this.setState(partial);
    }

    /**
     * @param {Array|boolean} catalog
     * @returns {boolean} true when copy and catalog disagree
     */
    checkUnmatchedCopy(catalog) {
        const unmatched = BrandService.unmatchedModuleCopy(catalog);
        if (!unmatched.length) {
            return false;
        }

        Toast.error(
            "The module catalog no longer matches this build (" +
                unmatched.join(", ") +
                "). Module settings are locked because their confirmation prompts are missing."
        );

        return true;
    }

    /**
     * Retry a whole failed load. Separate from reload because Index emits the
     * reload event from its own render, where a synchronous setState is illegal.
     */
    retryLoad() {
        this.setState({ loading: true, loadFailed: false }, this.reload);
    }

    /**
     * Retry only the module catalog. The brand itself loaded, so its row and any
     * edit in progress are left alone.
     */
    async retryCatalog() {
        this.setState({ updating: true });
        const catalog = await BrandService.getPillars();

        if (catalog === false) {
            Toast.error(
                "Could not load the module catalog. Module settings are unavailable."
            );
            this.safeSetState({ updating: false });
            return;
        }

        const copyMismatch = this.checkUnmatchedCopy(catalog);
        const pillars = await this.adjustAmazonVisibilitySuite(
            this.state.row,
            catalog
        );

        this.safeSetState({
            updating: false,
            catalogFailed: false,
            copyMismatch,
            pillars,
        });
    }

    reload() {
        Promise.all([
            BrandService.get(this.state.id),
            BrandService.getPillars(),
        ])
            .then(async ([data, catalog]) => {
                if (data === false) {
                    Toast.error(
                        "Something went wrong while retrieving data from this brand"
                    );
                    // Never fall through to the form: the row is still the
                    // constructor stub, so it would read as a blank brand.
                    this.safeSetState({ loading: false, loadFailed: true });
                    return;
                }

                const catalogFailed = catalog === false;
                if (catalogFailed) {
                    Toast.error(
                        "Could not load the module catalog. Module settings are unavailable."
                    );
                }

                const copyMismatch = this.checkUnmatchedCopy(catalog);

                const prettyData = this.polishData(data),
                    pillars = await this.adjustAmazonVisibilitySuite(
                        prettyData,
                        catalogFailed ? [] : catalog
                    );

                this.safeSetState({
                    loading: false,
                    loadFailed: false,
                    catalogFailed,
                    copyMismatch,
                    row: prettyData,
                    pillars,
                    defaultSelected:
                        data.organization !== null &&
                        typeof data.organization.id === "number"
                            ? [data.organization]
                            : [],
                    editing: false,
                });
            })
            .catch(() => {
                Toast.error("Something went wrong while loading this brand.");
                // The row was never committed, so this is a whole-tab failure
                // rather than a catalog one.
                this.safeSetState({ loading: false, loadFailed: true });
            });
    }

    componentWillUnmount() {
        this.mounted = false;
        // Named, because the handler-less form clears every listener for the
        // event and would take a sibling tab's subscription with it.
        Emitter.off("BRAND_PROFILE", this.reload);
    }

    componentDidMount() {
        this.mounted = true;
        // Index emits this from render, so a one-time listener is what keeps the
        // tab from reloading on every parent render. Retry calls reload directly.
        Emitter.once("BRAND_PROFILE", this.reload);
    }

    saveChanges() {
        this.setState({ updating: true });
        let payload = Object.assign({}, this.changes);
        let switchedWhereToBuy = null;

        if (
            Object.keys(payload.brand).length === 0 &&
            Object.keys(payload.store).length === 0 &&
            Object.keys(payload.fields).length === 0 &&
            Object.keys(payload.country).length === 0 &&
            payload.regions === null &&
            payload.categories === null
        ) {
            this.setState({ updating: false, editing: false });
            Toast.info("No modifications were made.");
            return;
        }

        // store settings
        let store = [];
        Object.keys(payload.store).forEach((key) => {
            let obj = payload.store[key];
            if (obj.id) {
                // there's an old value
                obj.oldValue =
                    this.storeSettings[this.mapStoreSettings[obj.name]].value;
            } else {
                obj.oldValue = "";
            }
            if (typeof this.modulesToUnassign[obj.name] !== "undefined") {
                obj.unassignFromProducts = this.modulesToUnassign[obj.name];
            }
            store.push(obj);
            if (obj.name === PartnerService.WHERE_TO_BUY_MODULE_NAME) {
                switchedWhereToBuy = obj.value === "on";
            }
        });
        payload.store = store;
        store = null;

        // brand settings
        let brand = [];
        Object.keys(payload.brand).forEach((key) => {
            let obj = payload.brand[key];
            if (obj.id) {
                // there's an old value
                obj.oldValue =
                    this.brandSettings[this.mapBrandSettings[obj.name]].value;
            } else {
                obj.oldValue = "";
            }
            if (typeof this.modulesToUnassign[obj.name] !== "undefined") {
                obj.unassignFromProducts = this.modulesToUnassign[obj.name];
            }
            brand.push(obj);
        });
        payload.brand = brand;
        brand = null;

        // brand_country settings
        let country = [];
        Object.keys(payload.country).forEach((key) => {
            const obj = payload.country[key],
                parts = key.split("-");

            let value = "";
            const oldValue = obj.id ? obj.id : "";

            if (obj.value) {
                if (Array.isArray(obj.value)) {
                    value = obj.value
                        .map((merchant) => merchant.id)
                        .filter((id) => id);
                } else if (typeof obj.value === "object" && obj.value.id) {
                    value = [obj.value.id];
                } else {
                    value = obj.value;
                }
            }

            let data = {
                name: parts[0],
                country_id: parseInt(parts[1]),
                oldValue: oldValue,
                value: value,
            };
            country.push(data);
        });
        payload.country = country;
        country = null;

        // fields settings
        let fields = [];
        Object.keys(payload.fields).forEach((key) => {
            let newValue = payload.fields[key],
                oldValue = this.original[key];
            fields.push({
                name: key,
                value: newValue,
                oldValue,
            });
        });
        payload.fields = fields;
        fields = null;

        // categories
        if (payload.categories !== null) {
            let originalCategories = this.original.categories.map(
                    (row) => row.value
                ),
                add = [],
                remove = [];

            payload.categories.forEach((id) => {
                if (originalCategories.indexOf(id) === -1) {
                    add.push(id);
                }
            });
            originalCategories.forEach((id) => {
                if (payload.categories.indexOf(id) === -1) {
                    remove.push(id);
                }
            });
            payload.categories = {};
            if (add.length) {
                payload.categories.add = add;
            }
            if (remove.length) {
                payload.categories.remove = remove;
            }
        } else {
            payload.categories = {};
        }

        // crawling regions
        if (payload.regions !== null) {
            let originalRegions = this.original.crawlingRegions.map(
                    (row) => row.value
                ),
                add = [],
                remove = [];

            payload.regions.forEach((id) => {
                if (originalRegions.indexOf(id) === -1) {
                    add.push(id);
                }
            });
            originalRegions.forEach((id) => {
                if (payload.regions.indexOf(id) === -1) {
                    remove.push(id);
                }
            });
            payload.regions = {};
            if (add.length) {
                payload.regions.add = add;
            }
            if (remove.length) {
                payload.regions.remove = remove;
            }
        } else {
            payload.regions = {};
        }

        BrandService.update(this.state.id, payload)
            .then(async (result) => {
                if (result === false) {
                    this.safeSetState({ updating: false, editing: true });
                    Toast.error(
                        "Something wrong happened and changes could not be saved."
                    );
                    return;
                }

                const row = this.polishData(result),
                    pillars = await this.adjustAmazonVisibilitySuite(row);
                this.safeSetState({
                    updating: false,
                    editing: false,
                    row,
                    pillars,
                });
                delete this.original;
                if (switchedWhereToBuy !== null) {
                    Emitter.emit(
                        "BRAND_WHERE_TO_BUY_SWITCH",
                        switchedWhereToBuy
                    );
                }
                Toast.ok("Changes properly saved.");
            })
            .catch((error) => {
                // Without this the form stays stuck in `updating`, with every
                // control disabled and no way back except a reload.
                console.error(error);
                this.safeSetState({ updating: false, editing: true });
                Toast.error(
                    "Something wrong happened and changes could not be saved."
                );
            });
    }

    startEdition() {
        this.original = JSON.parse(JSON.stringify(this.state.row));
        this.changes = {
            fields: {},
            store: {},
            brand: {},
            country: {},
            regions: null,
            categories: null,
        };
        this.modulesToUnassign = {};
        this.setState({
            editing: true,
            problems: this.validateRow(this.state.row),
        });
    }

    dropEdition() {
        this.setState({ editing: false, row: this.original });
        delete this.original;
    }

    PPDStatus() {
        const scheduled_pdd = this.state.row.scheduled_pdd ?? null;
        if (scheduled_pdd === null) {
            return (
                <div
                    className="disabled"
                    title="PPD Schedule API is not available"
                >
                    Unknown <FontAwesomeIcon icon={faQuestionCircle} />
                </div>
            );
        }
        return (
            <Form.Check
                type="switch"
                label={scheduled_pdd ? "On" : "Off"}
                name="scheduled_pdd"
                checked={scheduled_pdd}
                disabled={true}
            />
        );
    }

    render() {
        if (this.state.loading) {
            return (
                <Container className="pt-4">
                    <Row
                        style={{
                            height: "100px",
                        }}
                    >
                        <Col>
                            <Loading />
                        </Col>
                    </Row>
                </Container>
            );
        }

        if (this.state.loadFailed) {
            return (
                <Container className="pt-4">
                    <Alert
                        variant="danger"
                        className="d-flex align-items-center justify-content-between"
                    >
                        <span>
                            This brand could not be loaded, so nothing on this
                            tab is showing its real values.
                        </span>
                        <Button
                            variant="outline-danger"
                            onClick={this.retryLoad}
                        >
                            Retry
                        </Button>
                    </Alert>
                </Container>
            );
        }

        const setPillarDisplay = (pillar) => {
            const pillarSettings = pillar.modules;
            if (typeof pillarSettings === "undefined") {
                return;
            }

            let appsGroup = [];
            let settingsGroup = [];

            pillarSettings.forEach((app) => {
                const book = app.book;
                const appSettings = app.settings || [];

                appsGroup.push(
                    withInactiveHint(
                        <Form.Check
                            key={`app-${app.code}`}
                            // Without an id react-bootstrap leaves the label
                            // unassociated, so the switch has no accessible name.
                            // Scoped by pillar because a setting name may legally
                            // appear more than once in the catalog, and duplicate
                            // ids would bind a label to the wrong switch.
                            id={`module-${pillar.code}-${app.code}`}
                            type="switch"
                            label={app.label}
                            name={app.code}
                            book={book}
                            pillar={pillar.code}
                            checked={this.getSettingBool(
                                book,
                                app.code,
                                app.inverted
                            )}
                            disabled={!this.isEditable(app)}
                            onChange={(e) =>
                                this.handleChangeSetting(e, appSettings)
                            }
                        />,
                        `app-${app.code}`,
                        app.active === false
                    )
                );

                if (appSettings.length) {
                    let inner = [];

                    appSettings.forEach((setting) => {
                        const book = setting.book;
                        const inactive =
                            app.active === false || setting.active === false;
                        const shouldEnable =
                            this.isEditable(app) &&
                            this.isEditable(setting) &&
                            this.getSettingBool(
                                app.book,
                                app.code,
                                app.inverted
                            );

                        if (setting.type === "boolean") {
                            inner.push(
                                withInactiveHint(
                                    <Form.Check
                                        key={`setting-${setting.code}`}
                                        id={`setting-${pillar.code}-${app.code}-${setting.code}`}
                                        type="switch"
                                        label={setting.label}
                                        name={setting.code}
                                        book={book}
                                        pillar={pillar.code}
                                        checked={this.getSettingBool(
                                            book,
                                            setting.code,
                                            setting.inverted
                                        )}
                                        disabled={!shouldEnable}
                                        onChange={this.handleChangeSetting}
                                    />,
                                    `setting-${setting.code}`,
                                    inactive
                                )
                            );
                        } else if (setting.type === "text") {
                            inner.push(
                                withInactiveHint(
                                    <Row key={`setting-${setting.code}`}>
                                        <Col className="d-flex align-items-center left-content-center">
                                            {setting.label}
                                        </Col>
                                        <Col sm={7}>
                                            <Form.Control
                                                type="text"
                                                name={setting.code}
                                                book={book}
                                                value={this.getSetting(
                                                    book,
                                                    setting.code
                                                )}
                                                disabled={!shouldEnable}
                                                placeholder={
                                                    setting.placeholder
                                                }
                                                onChange={
                                                    this.handleChangeSetting
                                                }
                                            />
                                        </Col>
                                    </Row>,
                                    `setting-${setting.code}`,
                                    inactive,
                                    "w-100"
                                )
                            );
                        } else if (setting.type === "option") {
                            inner.push(
                                withInactiveHint(
                                    <Form.Group
                                        className="mt-3"
                                        key={`setting-${setting.code}`}
                                    >
                                        <Form.Label>{setting.label}</Form.Label>
                                        <Form.Select
                                            aria-label={setting.label}
                                            name={setting.code}
                                            book={book}
                                            disabled={!shouldEnable}
                                            onChange={this.handleChangeSetting}
                                            value={this.getSetting(
                                                book,
                                                setting.code
                                            )}
                                        >
                                            {setting.options.map((opt) => (
                                                <option
                                                    key={`opt-${setting.code}-${opt.value}`}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>,
                                    `setting-${setting.code}`,
                                    inactive,
                                    "w-100"
                                )
                            );
                        } else if (setting.type === "merchant") {
                            const key = `${setting.code}-${setting.country_id}`,
                                value = this.getSetting(book, key, 0);

                            let selected = [];
                            const MULTIPLE_MARKETPLACES = [
                                "amazon_merchant",
                                "ebay_merchants",
                                "walmart_merchants",
                            ];
                            let isMultiple = MULTIPLE_MARKETPLACES.includes(
                                setting.code
                            );

                            if (value !== null && value !== 0) {
                                if (value.multiple && value.merchants) {
                                    selected = value.merchants
                                        .filter(
                                            (merchant) =>
                                                merchant && merchant.id
                                        )
                                        .map((merchant) =>
                                            Object.assign({}, merchant, {
                                                combined:
                                                    MerchantSearch.formatOptionLabel(
                                                        merchant
                                                    ),
                                            })
                                        );
                                    isMultiple = true;
                                } else if (Array.isArray(value)) {
                                    selected = value
                                        .filter(
                                            (merchant) =>
                                                merchant && merchant.id
                                        )
                                        .map((merchant) =>
                                            Object.assign({}, merchant, {
                                                combined:
                                                    MerchantSearch.formatOptionLabel(
                                                        merchant
                                                    ),
                                            })
                                        );
                                } else if (value.id) {
                                    selected = [
                                        Object.assign({}, value, {
                                            combined:
                                                MerchantSearch.formatOptionLabel(
                                                    value
                                                ),
                                        }),
                                    ];
                                }
                            }

                            inner.push(
                                withInactiveHint(
                                    <Col
                                        key={`setting-${setting.code}-${setting.website_id}`}
                                        sm={12}
                                        className="left-content-center"
                                    >
                                        {setting.label}
                                        <MerchantSearch
                                            onFocus={() => {
                                                this.merchantOnFocus = key;
                                            }}
                                            marketplaceId={setting.website_id}
                                            onChange={this.handleMerchantChange}
                                            selected={selected}
                                            disabled={!shouldEnable}
                                            multiple={isMultiple}
                                        />
                                    </Col>,
                                    `setting-${setting.code}-${setting.website_id}`,
                                    inactive,
                                    "w-100"
                                )
                            );
                        } else {
                            // The catalog is served by the API, so a type this
                            // build does not render would otherwise be an
                            // invisible setting with no signal at all.
                            console.warn(
                                `Unsupported setting type "${setting.type}" for ${setting.code}; not rendered.`
                            );
                        }
                    });

                    if (inner.length) {
                        settingsGroup.push(
                            <div
                                key={`app-setting-${app.code}`}
                                className="settings__content"
                            >
                                <div className="settings__content__title">
                                    <span>{app.label}</span>
                                </div>
                                <div className="settings__content__content">
                                    {inner}
                                </div>
                            </div>
                        );
                    }
                }
            });

            return (
                <div className="pillar">
                    <div className="pillar__apps">{appsGroup}</div>
                    {!!settingsGroup.length && (
                        <div className="pillar__settings">
                            <div className="settings__title">
                                <span>Settings</span>
                            </div>
                            {settingsGroup}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <Container className="pt-4">
                <Row>
                    <Col sm={8}>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="store_name"
                            value={this.state.row.store_name}
                            readOnly={
                                !this.state.editing || this.state.updating
                            }
                            className={
                                this.state.problems !== false &&
                                this.state.problems.store_name
                                    ? "is-invalid"
                                    : ""
                            }
                            onChange={this.handleChange}
                        />
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Created</Form.Label>
                        <Form.Control
                            type="text"
                            name="created_at"
                            value={this.state.row.created_at}
                            disabled={true}
                        />
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Modified</Form.Label>
                        <Form.Control
                            type="text"
                            name="modified"
                            value={this.state.row.modified}
                            disabled={true}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={7}>
                        <Form.Label>Crawling countries</Form.Label>
                        <Regions
                            name="crawlingRegions"
                            value={this.state.row.crawlingRegions}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChangeRegions}
                        />
                    </Col>
                    <Col sm={5}>
                        <Form.Label>Customer Service Level</Form.Label>
                        <Form.Select
                            aria-label="Customer Service Level"
                            name="customer_service_level"
                            book="store"
                            value={this.getSetting(
                                "store",
                                "customer_service_level",
                                ""
                            )}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChangeSetting}
                        >
                            <option value="">Unknown</option>
                            <option value="Support Squad">Support Squad</option>
                            <option value="Account Management">
                                Account Management
                            </option>
                            <option value="Managed Services">
                                Managed Services
                            </option>
                        </Form.Select>
                    </Col>
                </Row>
                <Row>
                    <Col sm={4}>
                        <Form.Label>Organization</Form.Label>
                        <OrganizationSearch
                            defaultSelected={this.state.defaultSelected}
                            onChange={this.handleOrgChange}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            invalid={
                                this.state.problems !== false &&
                                this.state.problems.org_id
                            }
                        />
                    </Col>
                    <Col sm={3}>
                        <Form.Label>Sales Status</Form.Label>
                        <Form.Select
                            aria-label="Sales Status"
                            name="sales_status"
                            book="brand"
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChangeSetting}
                            value={this.getSetting("brand", "sales_status")}
                        >
                            <option value="Unknown">Unknown</option>
                            <option value="Demo - Build In Progress">
                                Demo - Build In Progress
                            </option>
                            <option value="Demo - Ready">Demo - Ready</option>
                            <option value="Demo - Brand Still Evaluating">
                                Demo - Brand Still Evaluating
                            </option>
                            <option value="Demo - Status Unknown">
                                Demo - Status Unknown
                            </option>
                            <option value="Demo - Did Not Convert">
                                Demo - Did Not Convert
                            </option>
                            <option value="Trial - Free">Trial - Free</option>
                            <option value="Trial - Free (Special Offer)">
                                Trial - Free (Special Offer)
                            </option>
                            <option value="Trial - Paid">Trial - Paid</option>
                            <option value="Trial - Did Not Convert">
                                Trial - Did Not Convert
                            </option>
                            <option value="Paying/Active">Paying/Active</option>
                            <option value="Delinquent">Delinquent</option>
                            <option value="Canceled - Churned/Lost">
                                Canceled - Churned/Lost
                            </option>
                            <option value="Products Moved">
                                Products Moved
                            </option>
                            <option value="Mistake / Duplicate">
                                Mistake / Duplicate
                            </option>
                            <option value="Paying/Active - Untracked Account">
                                Paying/Active - Untracked Account
                            </option>
                            <option value="Used for Testing">
                                Used for Testing
                            </option>
                            <option value="Demo - PDLT">Demo - PDLT</option>
                            <option value="Disabled - PDLT">
                                Disabled - PDLT
                            </option>
                        </Form.Select>
                    </Col>
                    <Col sm={3}>
                        <Form.Label>Onboarding Status</Form.Label>
                        <Form.Select
                            aria-label="Onboarding Status"
                            name="onboarding_status"
                            book="brand"
                            value={this.getSetting(
                                "brand",
                                "onboarding_status"
                            )}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChangeSetting}
                        >
                            <option value="Unknown">Unknown</option>
                            <option value="Onboarding - Welcome/Training">
                                Onboarding - Welcome/Training
                            </option>
                            <option value="Onboarding - Complete">
                                Onboarding - Complete
                            </option>
                        </Form.Select>
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Capture Screenshots</Form.Label>
                        <Form.Select
                            name="ss_every_product"
                            book="store"
                            value={this.getSetting("store", "ss_every_product")}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChangeSetting}
                        >
                            <option value="0">of Violations</option>
                            <option value="1">of Everything</option>
                        </Form.Select>
                    </Col>
                </Row>
                <Row>
                    <Col sm={2}>
                        <Form.Label>Status</Form.Label>
                        <Form.Check
                            type="switch"
                            label={
                                /(1|on)/i.test(this.state.row.store_enable)
                                    ? "Active"
                                    : "Disabled"
                            }
                            name="store_enable"
                            checked={/(1|on)/i.test(
                                this.state.row.store_enable
                            )}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChange}
                        />
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Scheduled PDD</Form.Label>
                        <this.PPDStatus />
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Review Crawling</Form.Label>
                        <Form.Check
                            type="switch"
                            label={
                                /(1|on)/i.test(this.state.row.reviews)
                                    ? "On"
                                    : "Off"
                            }
                            name="reviews"
                            checked={/(1|on)/i.test(this.state.row.reviews)}
                            // disabled={!this.state.editing || this.state.updating}
                            // disabled until we figure out where this is used
                            disabled={true}
                            onChange={this.handleChange}
                        />
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Force Fresh Screenshot</Form.Label>
                        <Form.Check
                            type="switch"
                            label={
                                /(1|on)/i.test(this.state.row.force_screenshot)
                                    ? "Yes"
                                    : "No"
                            }
                            name="force_screenshot"
                            checked={/(1|on)/i.test(
                                this.state.row.force_screenshot
                            )}
                            // disabled={!this.state.editing || this.state.updating}
                            // perhaps should not be enabled for non-devs
                            disabled={true}
                            onChange={this.handleChange}
                        />
                    </Col>
                    <Col sm={2}>
                        <Form.Label>Note Enabled</Form.Label>
                        <Form.Check
                            type="switch"
                            label={this.state.row.note_enable ? "Yes" : "No"}
                            name="note_enable"
                            checked={/(1|on)/i.test(this.state.row.note_enable)}
                            // disabled={!this.state.editing || this.state.updating}
                            // disabled until we figure out where this is being used
                            disabled={true}
                            onChange={this.handleChange}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <Form.Label>Internal Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            placeholder="Leave an internal note here"
                            name="notes"
                            book="store"
                            value={this.getSetting("store", "notes", "")}
                            disabled={
                                !this.state.editing || this.state.updating
                            }
                            onChange={this.handleChangeSetting}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} className="pillars">
                        {this.state.catalogFailed && (
                            <Alert
                                variant="danger"
                                className="d-flex align-items-center justify-content-between"
                            >
                                <span>
                                    Module settings could not be loaded, so they
                                    are not listed here. This brand may well
                                    have modules enabled — do not read this as
                                    an empty list.
                                </span>
                                <Button
                                    variant="outline-danger"
                                    disabled={this.state.updating}
                                    onClick={this.retryCatalog}
                                >
                                    Retry
                                </Button>
                            </Alert>
                        )}
                        {this.state.copyMismatch && (
                            <Alert variant="danger">
                                The module catalog no longer matches this build,
                                so the confirmation prompts that gate expensive,
                                irreversible modules are missing. Module
                                settings are shown read-only until the build
                                catches up.
                            </Alert>
                        )}
                        {!this.state.catalogFailed && (
                            <Accordion
                                defaultActiveKey={
                                    this.state.pillars.length
                                        ? this.state.pillars[0].code
                                        : null
                                }
                                flush
                            >
                                {this.state.pillars.map((obj) => (
                                    <Accordion.Item
                                        eventKey={obj.code}
                                        key={`pillar-${obj.code}`}
                                    >
                                        <Accordion.Header>
                                            {this.getPillarStatus(
                                                obj.modules
                                            ) && (
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    style={{
                                                        color: "#0c63e4",
                                                    }}
                                                />
                                            )}
                                            {obj.label}
                                        </Accordion.Header>

                                        <Accordion.Body>
                                            {setPillarDisplay(obj)}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Col sm={6}>
                        {!this.state.editing && (
                            <Button
                                variant="secondary"
                                onClick={this.startEdition}
                            >
                                Edit
                            </Button>
                        )}
                        {this.state.editing && (
                            <span>
                                <Button
                                    disabled={this.state.updating}
                                    variant="secondary"
                                    onClick={this.dropEdition}
                                >
                                    Cancel
                                </Button>
                                <ProcessingButton
                                    variant="primary"
                                    processing={this.state.updating}
                                    processingLabel="Saving ..."
                                    label="Save"
                                    disabled={this.state.problems !== false}
                                    onClick={this.saveChanges}
                                />
                            </span>
                        )}
                    </Col>
                </Row>
            </Container>
        );
    }
}

Profile.propTypes = {
    /** Brand ID */
    brandId: PropTypes.number.isRequired,
};

export default Profile;
